import type { Presentation } from '@features/powerpoint';
import { titleSlideV2 } from '../../../../slides/title-slide-v2';
import {
  createSharedPresentationConfigV2,
  MASTER_NAMES,
} from '../../../../utils/shared-presentation-config-v2';
import { sectionTitleSlideV2 } from '../../../../slides/shared/section-title-slide-v2';
import { groupRetailers } from '@features/campaigns/subdomains/cpg';
import { buildOverallRetailer } from '../../domain/build-overall-retailer';
import { overviewSlideFromPropsV2 } from './build-overview-slide';
import { executiveSummarySlideFromPropsV2 } from './build-executive-summary-slide';
import { salesLiftSummarySlideV2 } from '../../slides/v2/sales-lift-summary-slide';
import { newVsExistingBuyersSlideV2 } from '../../slides/v2/new-vs-existing-buyers-slide';
import { retailersSummarySlideV2 } from '../../slides/v2/retailers-summary-slide';
import { performanceByTopRetailersSlidesV2 } from '../../slides/v2/performance-by-top-retailers-slides';
import { appendixSlidesFromPropsV2 } from '../../slides/v2/appendix/config';
import type { CpgLciReportsFieldsData } from '../build-presentation';
import type { FormSchemaV2 } from '../../types/form-schema-v2';

export type BuildPresentationV2Props = {
  data: CpgLciReportsFieldsData;
  customFields: FormSchemaV2;
  features: Pick<Settings['features'], 'cpgSalesRate'>;
};

const salesRateExcludedColumns = (cpgSalesRate: boolean): string[] =>
  cpgSalesRate ? [] : ['salesRate'];

export const buildPresentationV2 = (
  props: BuildPresentationV2Props
): Presentation => {
  const {
    data,
    features,
    customFields: { includedSlides },
  } = props;

  const sharedConfig = createSharedPresentationConfigV2({
    campaign: data.campaign,
    brandLogo: data.brandLogo,
  });

  const groupedRetailers = groupRetailers(data.report.retailers);

  return {
    ...sharedConfig,
    filename: `${data.campaign.name}_lci_report_${data.selectedReportDate}.pptx`,
    slides: [
      titleSlideV2({
        campaign: { id: data.campaign.id, name: data.campaign.name },
        brandLogo: data.brandLogo,
      }),
      ...overviewSlideFromPropsV2(props, MASTER_NAMES.titleAndContent),
      ...executiveSummarySlideFromPropsV2(props, MASTER_NAMES.titleAndContent),
      ...(includedSlides.salesAttribution.titleSlide
        ? [sectionTitleSlideV2('Sales Lift')]
        : []),
      ...(includedSlides.salesAttribution.summary
        ? [
            salesLiftSummarySlideV2({
              slideMaster: MASTER_NAMES.titleAndContent,
              report: data.report,
              iroas: data.roasData.iroas,
              roas: data.roasData.roas,
              excludedColumns: salesRateExcludedColumns(features.cpgSalesRate),
            }),
          ]
        : []),
      ...(includedSlides.salesAttribution.newBuyerVsExistingBuyer
        ? [
            newVsExistingBuyersSlideV2({
              slideMaster: MASTER_NAMES.titleAndContent,
              newBuyerPercentage: data.report.newBuyerPercentage ?? 0,
              existingBuyerPercentage: data.report.existingBuyerPercentage ?? 0,
            }),
          ]
        : []),
      ...(includedSlides.performanceByRetailer.titleSlide
        ? [sectionTitleSlideV2('Performance by Retailer')]
        : []),
      ...(includedSlides.performanceByRetailer.retailerSummary
        ? [
            retailersSummarySlideV2({
              retailers: data.report.retailers,
              projectedIncrementalSales: data.report.projectedIncrementalSales,
              totalProjectedSales: data.report.totalProjectedSales,
              slideMaster: MASTER_NAMES.titleAndContent,
            }),
          ]
        : []),
      ...(includedSlides.performanceByRetailer.performanceByTopTwenty
        ? performanceByTopRetailersSlidesV2({
            slideMaster: MASTER_NAMES.titleAndContentNoLogo,
            groupedRetailers,
            overallRetailer: buildOverallRetailer(data.report),
            excludedColumns: salesRateExcludedColumns(features.cpgSalesRate),
          })
        : []),
      ...appendixSlidesFromPropsV2(props),
    ],
  };
};
